import React, { useMemo } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { ToastManager } from '@features';
import { getNameExtension } from '@shared/helpers';
import { useDnd } from '@shared/hooks';
import { BreadCrumbs, LoaderDots } from '@shared/ui';
import { CreateFolderButton } from '@widgets/Files/CreateFolderButton/CreateFolderButton';
import { SpinnerLoader } from '@widgets/Files/UI/FileCard/SpinnerLoader/SpinnerLoader';
import { useFiles } from '@widgets/Files/useFiles';

import { AddFilesButton } from './AddFilesButton/AddFilesButton';
import cls from './Files.module.css';
import { FilesListWrapper } from './FilesListWrapper/FilesListWrapper';
import { useFileUpload } from './hooks/useFileUpload';
import { ContextFiles } from './model/context';
import { ModalFiles } from './UI/Modals';
import { useAppSelector } from '../../store';

export const Files = () => {
    const { t } = useTranslation();
    const {
        contextFilesValue,
        modalMode,
        onHideModal,
        breadCrumbsData,
        parsedFilesData,
        isDataLoading,
        isFetching,
        // isFetchingFiles,
        // isUploading,
        isDeleting,
        isDownloading,
        onBreadCrumbDrop,
    } = useFiles();
    // const [isOpenPushNotification, setIsOpenPushNotification] = useState(false);
    const currentFolderName = useMemo(
        () =>
            breadCrumbsData?.find(
                (breadCrumb) => breadCrumb.id === contextFilesValue.currentFolderId
            )?.name || t('Files.FileNotificationsPush.fileText.allFiles'),
        [breadCrumbsData, contextFilesValue.currentFolderId, t]
    );
    const { onFileChange, isAwaitingUpload, setIsAwaitingUpload, setFileNames } =
        useFileUpload(currentFolderName);
    const {
        isFileOver,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDragStart,
        handleDrop,
    } = useDnd({
        mode: 'allFiles',
        onFileChange,
        acceptDrop: (e) => {
            const { types } = e.dataTransfer;
            return types.includes('Files');
        },
        currentFolderId: contextFilesValue.currentFolderId,
    });

    // const { needFetchMore } = useAppSelector((state) => state.filesReducer);
    const isLoadingMore = useAppSelector((state) => state.filesReducer.fetchFilesLoadingMore);

    // const onLoadNextPart = useCallback(() => {
    //     dispatch(loadMoreFiles());
    // }, [dispatch]);

    // const lastElementRef = useInfiniteScrollNew(onLoadNextPart);

    // const ids = useAppSelector(filesSelector.selectIds);

    const onFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
        handleDrop(e);
        const names = Array.from(e.dataTransfer.files).map((file) => {
            const { name } = getNameExtension(file.name);
            return name;
        });
        setFileNames(names);
        if (isAwaitingUpload) {
            setIsAwaitingUpload(true);
        }
    };

    return (
        <ContextFiles.Provider value={contextFilesValue}>
            {(isDataLoading || isFetching || isDeleting || isDownloading) && (
                <LoaderDots disableBackdrop={isFetching} />
            )}
            <ModalFiles mode={modalMode} onHideModal={onHideModal} />
            {/* <div className={cls.headerBtnsWrapper}> */}
            {/*    <AddFilesButton onFileChange={onFileChange} /> */}
            {/*    <CreateFolderButton /> */}
            {/* </div> */}
            <BreadCrumbs
                currentFolderId={contextFilesValue.currentFolderId || null}
                onClickBreadCrumb={contextFilesValue.onBreadCrumbClick}
                breadCrumbs={breadCrumbsData || []}
                classes={cls.BreadCrumbsMargin}
                onDrop={onBreadCrumbDrop}
            />
            <div
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={onFileDrop}
                className={cls.dndContainer}
            >
                <div className={classNames(cls.Files)}>
                    {isFileOver && (
                        <div className={cls.addFileArea}>
                            <div className={cls.addFileTitle}>{t('Files.return.addFileTitle')}</div>
                        </div>
                    )}
                    <div className={cls.headerBtnsWrapper}>
                        <AddFilesButton onFileChange={onFileChange} />
                        <CreateFolderButton />
                    </div>
                    <FilesListWrapper parsedFilesData={parsedFilesData} />
                </div>
            </div>
            {/* {needFetchMore && !!ids.length && <div ref={lastElementRef} />} */}
            {isLoadingMore && (
                <div className={cls.moreLoader}>
                    <SpinnerLoader />
                </div>
            )}
            <ToastManager toastPosition="left" />
        </ContextFiles.Provider>
    );
};
